// Pure rules behind the cross-channel packing round on /seller/orders
// (design docs/specs/2026-10-04-seller-orders-packing-round-design.md).
//
// WHY A MODULE AND NOT COMPUTED PROPERTIES. This repo's jest cannot mount a
// .vue file (reference-bo-fn-jest-infra), so every rule an operator can be
// wrong about lives here and Orders.vue only renders it. Behaviour is specified
// in tests/unit/sellerPackingRound.spec.js; the template itself is pinned by
// tests/unit/sellerPackingRoundSource.spec.js.
//
// --------------------------------------------------------------------------
// WHAT THIS FILE OWNS
//
// 1. The count chips (spec §6.1). The round is cut by `platform_id = 9`, NOT by
//    `bo_sale_channel_id`, so an order whose channel label never resolved
//    (`bo_sale_channel_id = 0`, the cause-B backlog) is STILL cut. It therefore
//    has to be visible: it becomes the "ไม่ระบุช่องทาง" chip and it counts
//    toward the total. Hiding it is how stock goes missing with nobody looking.
//
// 2. `ambiguous` (spec §4.1). When the owner maps two or more of web/member/app
//    onto one sale channel, the BE cannot tell them apart and returns the SAME
//    count for each — printing three chips there would triple the real number.
//    In that mode only the total is shown.
//
// 3. The look-back window. The core clamps 1..30 with a default of 5
//    (BO-Packing.go:395-399); the dropdown offers 1/3/5/7/14/30. The clamp is
//    re-done here so a stale value in component state cannot ask for a window
//    the server will silently change under it — the preview count and the cut
//    count must come from the same number (spec §4.1, AC 9).
//
// 4. The POST payload and the result mapper. The numbers shown AFTER the cut
//    come from rows that really entered the round, never from the preview
//    (spec §4.2) — so the mapper reads the response and nothing else.
// --------------------------------------------------------------------------

/** The three App sources the BE names in `packing_preview.channels[].source`. */
export const CHANNEL_SOURCES = ['shop', 'member', 'app'];

/** Look-back choices in the modal dropdown (spec §6.4 item 2). */
export const LOOKBACK_OPTIONS = [1, 3, 5, 7, 14, 30];

/** Core default / clamp, mirroring BO-Packing.go:395-399. */
export const DEFAULT_BACK_DAY = 5;
export const MIN_BACK_DAY = 1;
export const MAX_BACK_DAY = 30;

/** "all payment types" — the modal's default (owner decision, spec §9 item 5). */
export const PAYMENT_TYPE_ALL = 0;

/** Pack-room deep link for one round. */
export function packingRoundUrl(id) {
  const n = Number(id || 0);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `/backoffice/packings?bo_packing_id=${n}`;
}

/**
 * clampBackDay — 1..30, with 0 / blank / junk falling back to 5.
 *
 * `0` is NOT "no window": the core reads it as unset and substitutes 5, so
 * returning 0 here would make the screen promise a window the cut will not use.
 * Fractions are floored because `created_at >= now - N day` is a whole-day
 * window on the server.
 */
export function clampBackDay(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_BACK_DAY;
  if (n < MIN_BACK_DAY) return MIN_BACK_DAY;
  if (n > MAX_BACK_DAY) return MAX_BACK_DAY;
  return n;
}

/** Non-negative integer, with anything unreadable reading as 0. */
function toCount(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Payment-method type id, 0 = all. Negative / junk collapses to 0. */
export function normalizePaymentType(value) {
  return toCount(value);
}

const CHANNEL_META = {
  shop: { labelKey: 'seller_orders.chip_web', badgeClass: 'badge-info' },
  member: { labelKey: 'seller_orders.chip_member', badgeClass: 'badge-primary' },
  app: { labelKey: 'seller_orders.chip_app', badgeClass: 'badge-success' }
};

const UNASSIGNED_META = {
  labelKey: 'seller_orders.chip_unassigned',
  badgeClass: 'badge-secondary'
};

/**
 * channelBadge — the สถานะ-style pill in the ช่องทาง column (spec §6.3).
 *
 * Driven by `row.source_channel` (the stable `web|member|app` token) and not by
 * `channel_name`, which is a free-text label the owner can rename in
 * `bo_sync_sale_channel_map` at any time. An unknown or empty token is the
 * cause-B order: it gets the neutral "ไม่ระบุช่องทาง" pill rather than being
 * drawn as one of the three.
 */
// Badge-only channels: shown in the ช่องทาง column but never a preview chip,
// because the packing preview only counts shop / member / app.
const BADGE_ONLY_META = {
  chat: { labelKey: 'seller_orders.chip_chat', badgeClass: 'badge-warning' }
};

export function channelBadge(source) {
  const key = String(source || '').trim().toLowerCase();
  // The App writes `web` for the storefront; the BE's preview calls it `shop`.
  // `guest` (guest checkout) is the storefront too — the BE files it under
  // Storefront in controllers/BO-SaleChannel.go:30 (design 2026-10-05 §0 item 4).
  const canonical = key === 'web' || key === 'guest' ? 'shop' : key;
  const meta = CHANNEL_META[canonical] || BADGE_ONLY_META[canonical];
  if (!meta) return { source: '', labelKey: UNASSIGNED_META.labelKey, badgeClass: UNASSIGNED_META.badgeClass };
  return { source: canonical, labelKey: meta.labelKey, badgeClass: meta.badgeClass };
}

/**
 * previewFromListResponse — read `data.packing_preview` out of GET /seller/orders.
 *
 * Tolerates the pre-spec API (no `packing_preview` at all): the caller gets a
 * zeroed preview flagged `present: false`, which the page renders as "—" with
 * the cut button disabled rather than as a confident "0 orders ready".
 */
export function previewFromListResponse(res) {
  const body = (res && res.data && res.data.data) || {};
  return normalizePreview(body.packing_preview, { present: !!body.packing_preview });
}

/**
 * normalizePreview — one shape for `packing_preview`, whatever the API sent.
 *
 * Per-source counts are keyed by `source` and not by array position: the BE
 * omits a source entirely when it has no orders, and reading channels[1] as
 * "member" would then attribute app orders to members.
 */
export function normalizePreview(raw, opts) {
  const src = raw || {};
  const present = opts && 'present' in opts ? !!opts.present : !!raw;
  const list = Array.isArray(src.channels) ? src.channels : [];

  const bySource = {};
  CHANNEL_SOURCES.forEach(s => {
    bySource[s] = { source: s, label: '', boSaleChannelId: 0, count: 0 };
  });
  list.forEach(c => {
    const s = String((c && c.source) || '').trim().toLowerCase();
    const canonical = s === 'web' ? 'shop' : s;
    if (!bySource[canonical]) return;
    bySource[canonical] = {
      source: canonical,
      label: String((c && c.label) || ''),
      boSaleChannelId: toCount(c && c.bo_sale_channel_id),
      count: toCount(c && c.count)
    };
  });

  const unassignedCount = toCount(src.unassigned_count);
  const ambiguous = src.ambiguous === true;
  const partsSum =
    CHANNEL_SOURCES.reduce((acc, s) => acc + bySource[s].count, 0) + unassignedCount;

  // The server's own total is authoritative — it is the number of rows the cut
  // will touch (spec §4.1: "ผลรวมบนจอ = จำนวนที่ตัดจริงเสมอ"). When it is
  // missing, the parts are all we have. `countsConsistent` exposes a BE that
  // disagrees with itself instead of quietly papering over it.
  const hasServerTotal = typeof src.total_count === 'number' || typeof src.total_count === 'string';
  const serverTotal = hasServerTotal ? toCount(src.total_count) : null;
  const totalCount = serverTotal === null ? partsSum : serverTotal;

  return {
    present,
    backDay: clampBackDay(src.back_day),
    paymentMethodType: normalizePaymentType(src.payment_method_type),
    channels: CHANNEL_SOURCES.map(s => bySource[s]),
    bySource,
    unassignedCount,
    totalCount,
    partsSum,
    // `ambiguous` duplicates one count across sources, so the parts are not
    // meant to add up there and the flag would be noise.
    countsConsistent: ambiguous ? true : partsSum === totalCount,
    ambiguous,
    noWmsSummaryCount: readNoWmsSummary(src)
  };
}

/**
 * The unmapped-warehouse count, read from either name the contract uses:
 * `no_wms_summary_count` (spec §4.2) or a `warnings.no_wms_summary` envelope.
 * Owner decision (spec §9 item 1): these orders ARE cut — the Amaze Phase 0
 * trap treats an order with no `wms_product_summary` as instantly packed, so
 * holding them back silently is worse than cutting them and saying so.
 */
function readNoWmsSummary(src) {
  if (!src) return 0;
  if (src.no_wms_summary_count !== undefined) return toCount(src.no_wms_summary_count);
  const w = src.warnings;
  if (w && w.no_wms_summary !== undefined) return toCount(w.no_wms_summary);
  return 0;
}

/**
 * previewChips — exactly what the chip row renders (spec §6.1).
 *
 * - `ambiguous` → the three per-channel chips are dropped and an info chip
 *   explains why, so a tripled number can never be read as three channels.
 * - the "ไม่ระบุช่องทาง" chip appears only when it has orders in it, and
 *   carries its tooltip key so staff learn those orders ARE being cut.
 * - the total chip is always last and always present, including at 0.
 */
export function previewChips(preview) {
  const p = preview && preview.present !== undefined ? preview : normalizePreview(preview);
  const chips = [];
  if (!p.ambiguous) {
    CHANNEL_SOURCES.forEach(s => {
      const meta = CHANNEL_META[s];
      chips.push({
        key: s,
        labelKey: meta.labelKey,
        count: p.bySource[s].count,
        type: '',
        hintKey: ''
      });
    });
    if (p.unassignedCount > 0) {
      chips.push({
        key: 'unassigned',
        labelKey: UNASSIGNED_META.labelKey,
        count: p.unassignedCount,
        type: 'warning',
        hintKey: 'seller_orders.chip_unassigned_hint'
      });
    }
  } else {
    chips.push({
      key: 'ambiguous',
      labelKey: 'seller_orders.chip_ambiguous',
      count: null,
      type: 'info',
      hintKey: ''
    });
  }
  chips.push({
    key: 'total',
    labelKey: 'seller_orders.chip_total',
    count: p.totalCount,
    type: 'primary',
    // Counted on bo_orders inside the look-back window, so it can be lower
    // than the status-tab number (design 2026-10-05 §1.1 edge case 1).
    hintKey: 'seller_orders.chip_scope_hint'
  });
  return chips;
}

/**
 * previewRows — the read-only 2-column table in the modal (spec §6.4 item 4).
 *
 * Same data as the chips, minus the chrome: one row per source (plus unassigned
 * when non-zero), then a bold total row. In `ambiguous` mode the per-source
 * rows collapse to a single explanatory row for the same reason as the chips.
 */
export function previewRows(preview) {
  const p = preview && preview.present !== undefined ? preview : normalizePreview(preview);
  const rows = [];
  if (p.ambiguous) {
    rows.push({ key: 'ambiguous', labelKey: 'seller_orders.chip_ambiguous', count: null, total: false });
  } else {
    CHANNEL_SOURCES.forEach(s => {
      rows.push({ key: s, labelKey: CHANNEL_META[s].labelKey, count: p.bySource[s].count, total: false });
    });
    if (p.unassignedCount > 0) {
      rows.push({
        key: 'unassigned',
        labelKey: UNASSIGNED_META.labelKey,
        count: p.unassignedCount,
        total: false
      });
    }
  }
  rows.push({ key: 'total', labelKey: 'seller_orders.cutoff_total', count: p.totalCount, total: true });
  return rows;
}

/** RFC-4122 v4, from the platform CSPRNG when there is one. */
export function newIdempotencyKey() {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  const c = g.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint8Array(16);
    c.getRandomValues(buf);
    for (let i = 0; i < 16; i += 1) bytes[i] = buf[i];
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  /* eslint-disable no-bitwise */
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  /* eslint-enable no-bitwise */
  const hex = bytes.map(b => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join('')
  ].join('-');
}

/**
 * packingRoundPayload — the POST body (spec §4.2).
 *
 * Carries NO channel and NO carrier: this round covers every App channel at
 * once and the pack room picks the carrier (owner's instruction, spec §0).
 * `idempotency_key` is mandatory — it is what turns a double-click into one
 * round plus a 409 instead of two rounds (spec §3 edge case 7), so it is
 * generated here when the caller did not bring one.
 */
export function packingRoundPayload(input) {
  const src = input || {};
  return {
    back_day: clampBackDay(src.backDay !== undefined ? src.backDay : src.back_day),
    bo_payment_method_type_id: normalizePaymentType(
      src.paymentMethodType !== undefined ? src.paymentMethodType : src.bo_payment_method_type_id
    ),
    idempotency_key: String(src.idempotencyKey || src.idempotency_key || '') || newIdempotencyKey()
  };
}

/**
 * packingRoundResult — map the 200 body of POST …/packing-round (spec §4.2).
 *
 * Every number here is counted by the BE from `bo_packing_id = <id>` AFTER the
 * commit, so this mapper must not fall back to preview values: the figure the
 * operator reads in the success toast is the figure that actually moved.
 * `packing_url` is honoured when sent and rebuilt from the id when not, so the
 * "ไปห้องแพ็ค" button is never dead.
 */
export function packingRoundResult(res) {
  const data = (res && res.data && res.data.data) || (res && res.data) || {};
  const id = toCount(data.id);
  const list = Array.isArray(data.channels) ? data.channels : [];
  const bySource = {};
  CHANNEL_SOURCES.forEach(s => {
    bySource[s] = 0;
  });
  list.forEach(c => {
    const s = String((c && c.source) || '').trim().toLowerCase();
    const canonical = s === 'web' ? 'shop' : s;
    if (!(canonical in bySource)) return;
    bySource[canonical] = toCount(c && c.count);
  });
  return {
    id,
    cnt: toCount(data.cnt),
    shopCount: bySource.shop,
    memberCount: bySource.member,
    appCount: bySource.app,
    unassignedCount: toCount(data.unassigned_count),
    noWmsSummaryCount: readNoWmsSummary(data),
    packingUrl: String(data.packing_url || '') || packingRoundUrl(id)
  };
}

/**
 * successMessageArgs — the {id,cnt,web,member,app} the success toast interpolates.
 * Split out so the i18n parity spec can assert `cutoff_success_all` names every
 * placeholder this produces; a missing one renders as a literal `{app}`.
 */
export function successMessageArgs(result) {
  const r = result || {};
  return {
    id: r.id || 0,
    cnt: r.cnt || 0,
    web: r.shopCount || 0,
    member: r.memberCount || 0,
    app: r.appCount || 0
  };
}

const STATUS_ERROR_KEYS = {
  409: 'errors.cutoff_in_progress',
  422: 'errors.no_orders'
};

/**
 * packingRoundErrorKey — the i18n key for a failed cut, or '' when the caller
 * should just show the server's own message.
 *
 * The API answers `{"Status":"error","Msg":"errors.xxx"}` (capital Msg — see
 * src/util/api-error.js), so the body normally carries the key already and
 * `apiErrorMessage(err, this.$t)` translates it. This exists for the case the
 * body is empty or truncated: a bare 409 still has to read "a cut is already
 * running" and a bare 422 still has to read "ไม่พบสินค้า", because a raw
 * "Request failed with status code 409" tells an operator nothing.
 */
export function packingRoundErrorKey(err) {
  const res = err && err.response;
  if (!res) return '';
  const data = res.data || {};
  const raw = String(data.Msg || data.message || '').trim();
  if (raw.indexOf('errors.') === 0) return raw.replace(/:\s*$/, '');
  return STATUS_ERROR_KEYS[res.status] || '';
}

/**
 * paymentTypeOptions — the dropdown list (spec §6.2 / §6.4 item 3).
 *
 * Built from the existing store getter `PaymentMethod/getPaymentMethodTypeTableWithAll`
 * so the ids stay pinned to controllers/const.go:199-206 in one place. The only
 * change is id 0's label: the store spells it "--- ประเภทการชำระเงิน --- ",
 * which reads as a placeholder, while here 0 is a real and default choice
 * meaning "every payment type" — so the caller passes the translated "ทั้งหมด".
 */
export function paymentTypeOptions(storeList, allLabel) {
  const list = Array.isArray(storeList) ? storeList : [];
  const label = String(allLabel || '').trim();
  const out = [];
  let sawAll = false;
  list.forEach(o => {
    const id = toCount(o && o.id);
    if (id === PAYMENT_TYPE_ALL) {
      if (sawAll) return;
      sawAll = true;
      out.push({ id: PAYMENT_TYPE_ALL, name: label || String((o && o.name) || '') });
      return;
    }
    out.push({ id, name: String((o && o.name) || '') });
  });
  if (!sawAll) out.unshift({ id: PAYMENT_TYPE_ALL, name: label });
  return out;
}

/** The ประเภทชำระเงิน cell (spec §6.3). 0 / unknown reads as a dash, not as "all". */
export function paymentTypeName(storeList, id) {
  const n = toCount(id);
  if (n === PAYMENT_TYPE_ALL) return '-';
  const list = Array.isArray(storeList) ? storeList : [];
  const hit = list.find(o => toCount(o && o.id) === n);
  return hit && hit.name ? String(hit.name) : '-';
}

/**
 * previewQueryParams — the extra query the list call sends so that
 * `packing_preview` is counted with the same window the cut will use
 * (spec §4.1: the server echoes both back for exactly this reason).
 */
export function previewQueryParams(input) {
  const src = input || {};
  return {
    packing_back_day: clampBackDay(src.backDay !== undefined ? src.backDay : src.back_day),
    payment_method_type: normalizePaymentType(
      src.paymentMethodType !== undefined ? src.paymentMethodType : src.payment_method_type
    )
  };
}

export default {
  CHANNEL_SOURCES,
  LOOKBACK_OPTIONS,
  DEFAULT_BACK_DAY,
  MIN_BACK_DAY,
  MAX_BACK_DAY,
  PAYMENT_TYPE_ALL,
  packingRoundUrl,
  clampBackDay,
  normalizePaymentType,
  channelBadge,
  previewFromListResponse,
  normalizePreview,
  previewChips,
  previewRows,
  newIdempotencyKey,
  packingRoundPayload,
  packingRoundResult,
  successMessageArgs,
  packingRoundErrorKey,
  paymentTypeOptions,
  paymentTypeName,
  previewQueryParams
};
